/**
 * Quest turn-in reward fit — shared by the server's turn-in gate and the
 * client's quest dialog (plan 06, 2026-10-07).
 *
 * Simulates the turn-in without touching live state: collect-objective items
 * are consumed first, then every reward item is added, and the result is
 * counted in bag slots (`src/shared/inventory/bagCapacity.ts`). A reward that
 * duplicates an item already carried simply takes another slot.
 */

import type { QuestDefinition } from '../content/generated/quests.gen';
import { ITEM_DATABASE } from '../ItemData';
import {
  DEFAULT_BAG_SLOTS,
  planBagChange,
  type BagLike,
  type BagPlan,
} from '../inventory/bagCapacity';
import { questRewardItemsFor } from './questRewardItems';

type QuestRewardShape = Pick<QuestDefinition, 'objectives' | 'rewards'> & Partial<Pick<QuestDefinition, 'rewardClassItems'>>;

/** Reward quantity as the runtime grant applies it (absent/invalid → 1). */
export function questRewardQuantity(quantity: unknown): number {
  return typeof quantity === 'number' && quantity > 0 ? quantity : 1;
}

export function planQuestRewards(
  inventory: BagLike | undefined | null,
  quest: QuestRewardShape,
  maxSlots: number = DEFAULT_BAG_SLOTS,
  /** The hero's class: its `rewardClassItems` entry takes a slot too. */
  heroClass?: string | null,
): BagPlan {
  const remove = (quest.objectives ?? [])
    .filter(objective => objective.type === 'collect')
    .map(objective => ({ id: objective.targetId, quantity: objective.targetCount }));
  const add = questRewardItemsFor(quest, heroClass)
    .filter(reward => !!reward.id)
    .map(reward => ({
      id: reward.id,
      quantity: questRewardQuantity(reward.quantity),
      maxStack: reward.maxStack,
    }));
  return planBagChange(inventory, { add, remove }, maxSlots);
}

/** Boolean form of `planQuestRewards`. */
export function canFitQuestRewards(
  inventory: BagLike | undefined | null,
  quest: QuestRewardShape,
  maxSlots: number = DEFAULT_BAG_SLOTS,
  heroClass?: string | null,
): boolean {
  return planQuestRewards(inventory, quest, maxSlots, heroClass).ok;
}

/**
 * Player-facing reason a turn-in doesn't fit, e.g.
 * "Your bag is full: free 1 slot for Cloth Gloves, then turn in again."
 * Used by the chat failure line and the quest dialog.
 */
export function describeRewardBlock(block: { blockingItemIds?: readonly string[]; missingSlots?: number }): string {
  const names = (block.blockingItemIds ?? [])
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
    .map(id => (ITEM_DATABASE as Record<string, { name?: string } | undefined>)[id]?.name || id);
  const missing = typeof block.missingSlots === 'number' && Number.isFinite(block.missingSlots) && block.missingSlots > 0
    ? Math.ceil(block.missingSlots)
    : Math.max(1, names.length);
  const slots = `${missing} slot${missing === 1 ? '' : 's'}`;
  const forWhat = names.length > 0 ? ` for ${names.join(', ')}` : '';
  return `Your bag is full: free ${slots}${forWhat}, then turn in again. Your quest progress and items have been kept.`;
}
